<script setup>
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const { tenant, emailing, callback } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    emailing: {
        type: Object,
        required: false,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const emailingExclusionTypes = [
    { label: 'Email', value: 'email' },
    { label: 'Domaine', value: 'domain' },
];

const isOpen = ref(false);

const isSubmitting = ref(false);

const isLoading = ref(false);

const form = reactive({
    values: '',
    type: emailingExclusionTypes[0],
});

const validationSchema = object({
    values: string()
        .required('Les valeurs sont requises')
        .test(
            'at-least-one-value',
            'Veuillez entrer au moins une valeur.',
            (value) => {
                return value.split('\n').some((line) => line.trim() !== '');
            },
        ),
    type: object().required('Le type est requis'),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.modules.postImportEmailingExclusions',
                {
                    tenant: tenant.id,
                },
            ),
            {
                values: form.values
                    .split('\n')
                    .filter((value) => value.trim() !== ''),
                type: form.type.value,
                emailingId: emailing ? emailing.id : null,
            },
        )
        .then(() => {
            isOpen.value = false;

            if (callback) {
                callback();
            }
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            console.error(error);
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Importer </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <TextareaField
                            v-model="form.values"
                            label="Valeurs"
                            field-name="values"
                            field-id="values"
                            legend="Entrez une valeur par ligne"
                            required
                        />
                    </FormContent>

                    <FormContent>
                        <ListboxField
                            v-model="form.type"
                            field-id="type"
                            field-name="type"
                            label="Type"
                            :options="emailingExclusionTypes"
                            required
                        >
                            <template #buttonContent>
                                {{ form.type.label }}
                            </template>

                            <template #optionContent="{ option }">
                                {{ option.label }}
                            </template>
                        </ListboxField>
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    :disabled="isLoading"
                    submit-text="Importer"
                    submitting-text="Importation en cours..."
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
